import { useState } from 'react';
import { Globe, Linkedin, Factory, MapPin, Package, Sparkles, AlertCircle, ArrowRight } from 'lucide-react';
import type { OnboardingInput } from '../../types/settings';

const COMMON_COUNTRIES = [
  'United States', 'United Kingdom', 'United Arab Emirates', 'Canada', 'Australia',
  'Germany', 'France', 'Netherlands', 'Ireland', 'Switzerland',
  'Sweden', 'Norway', 'Denmark', 'Finland', 'Belgium',
  'Spain', 'Italy', 'Portugal', 'Austria', 'Poland',
  'India', 'Singapore', 'Hong Kong', 'Japan', 'South Korea',
  'Saudi Arabia', 'Qatar', 'Bahrain', 'Kuwait', 'Oman',
  'South Africa', 'Nigeria', 'Kenya', 'Egypt', 'Morocco',
  'Brazil', 'Mexico', 'Colombia', 'Argentina', 'Chile',
  'Israel', 'Turkey', 'New Zealand',
];

const COMMON_INDUSTRIES = [
  'Marketing & Advertising', 'HR & Recruitment', 'SaaS', 'Software Development',
  'Finance & Accounting', 'Banking & Financial Services', 'Insurance',
  'Healthcare & Medical', 'Pharmaceutical', 'Biotechnology',
  'Real Estate', 'Construction & Engineering', 'Architecture',
  'Logistics & Transportation', 'E-Commerce', 'Retail',
  'Manufacturing', 'Automotive', 'Legal Services',
  'Consulting & Professional Services', 'Education & Training',
  'Telecommunications', 'Information Technology', 'Cybersecurity',
  'Energy & Utilities', 'Hospitality & Tourism', 'Food & Beverage',
];

interface CompanyResearchOnboardingProps {
  onStartResearch: (input: OnboardingInput) => void;
  onSkip: () => void;
  previousInput?: OnboardingInput | null;
  previousError?: string | null;
}

export function CompanyResearchOnboarding({
  onStartResearch,
  onSkip,
  previousInput,
  previousError,
}: CompanyResearchOnboardingProps) {
  const [websiteUrl, setWebsiteUrl] = useState(previousInput?.website_url || '');
  const [linkedinUrl, setLinkedinUrl] = useState(previousInput?.linkedin_url || '');
  const [industry, setIndustry] = useState(previousInput?.industry || '');
  const [country, setCountry] = useState(previousInput?.country || '');
  const [productsServices, setProductsServices] = useState(previousInput?.products_services || '');
  const [showIndustrySuggestions, setShowIndustrySuggestions] = useState(false);
  const [showCountrySuggestions, setShowCountrySuggestions] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const filteredIndustries = COMMON_INDUSTRIES.filter(
    i => i.toLowerCase().includes(industry.toLowerCase())
  );

  const filteredCountries = COMMON_COUNTRIES.filter(
    c => c.toLowerCase().includes(country.toLowerCase())
  );

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!websiteUrl.trim()) {
      errors.website = 'Website URL is required';
    } else {
      try {
        const url = websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`;
        new URL(url);
      } catch {
        errors.website = 'Please enter a valid URL';
      }
    }

    if (!productsServices.trim()) {
      errors.products = 'Please describe your products or services';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const url = websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`;

    onStartResearch({
      website_url: url,
      linkedin_url: linkedinUrl.trim(),
      industry: industry.trim(),
      country: country.trim(),
      products_services: productsServices.trim(),
    });
  };

  return (
    <div className="py-4">
      <div>
        <div className="mb-5">
          <h3 className="text-lg font-extrabold text-gray-900 mb-1 font-display">
            Let's get to know your company
          </h3>
          <p className="text-sm text-gray-500">
            Our AI Agent will research your website and market to automatically build your sales playbook.
          </p>
        </div>

        {previousError && (
          <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Previous research failed</p>
              <p className="text-sm text-red-600 mt-0.5">{previousError}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border-2 border-gray-200 p-5 shadow-lg space-y-5">
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                <Globe className="w-4 h-4 text-[#1875FF]" />
                Website
                <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={websiteUrl}
                onChange={(e) => {
                  setWebsiteUrl(e.target.value);
                  if (validationErrors.website) setValidationErrors(prev => ({ ...prev, website: '' }));
                }}
                placeholder="https://yourcompany.com"
                className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm ${
                  validationErrors.website ? 'border-red-300 bg-red-50/50' : 'border-gray-300'
                }`}
              />
              {validationErrors.website && (
                <p className="text-xs text-red-500 mt-0.5">{validationErrors.website}</p>
              )}
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                <Linkedin className="w-4 h-4 text-[#1875FF]" />
                LinkedIn Page
                <span className="text-xs text-gray-400 font-normal">(opt.)</span>
              </label>
              <input
                type="text"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="linkedin.com/company/..."
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm"
              />
            </div>

            <div className="relative">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                <Factory className="w-4 h-4 text-[#1875FF]" />
                Industry
              </label>
              <input
                type="text"
                value={industry}
                onChange={(e) => {
                  setIndustry(e.target.value);
                  setShowIndustrySuggestions(e.target.value.length > 0);
                }}
                onFocus={() => industry.length > 0 && setShowIndustrySuggestions(true)}
                onBlur={() => setTimeout(() => setShowIndustrySuggestions(false), 200)}
                placeholder="e.g. SaaS"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm"
              />
              {showIndustrySuggestions && filteredIndustries.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl max-h-48 overflow-auto">
                  {filteredIndustries.map((item, idx) => (
                    <button
                      key={item}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setIndustry(item);
                        setShowIndustrySuggestions(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                <MapPin className="w-4 h-4 text-[#1875FF]" />
                Country
              </label>
              <input
                type="text"
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                  setShowCountrySuggestions(e.target.value.length > 0);
                }}
                onFocus={() => country.length > 0 && setShowCountrySuggestions(true)}
                onBlur={() => setTimeout(() => setShowCountrySuggestions(false), 200)}
                placeholder="e.g. United States"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm"
              />
              {showCountrySuggestions && filteredCountries.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl max-h-48 overflow-auto">
                  {filteredCountries.map((item, idx) => (
                    <button
                      key={item}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setCountry(item);
                        setShowCountrySuggestions(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
              <Package className="w-4 h-4 text-[#1875FF]" />
              Products / Services
              <span className="text-red-400">*</span>
            </label>
            <textarea
              value={productsServices}
              onChange={(e) => {
                setProductsServices(e.target.value);
                if (validationErrors.products) setValidationErrors(prev => ({ ...prev, products: '' }));
              }}
              placeholder="Briefly describe what you sell, e.g. AI-powered sales automation for B2B companies..."
              rows={3}
              className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all resize-none text-sm ${
                validationErrors.products ? 'border-red-300 bg-red-50/50' : 'border-gray-300'
              }`}
            />
            {validationErrors.products && (
              <p className="text-xs text-red-500 mt-0.5">{validationErrors.products}</p>
            )}
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={handleSubmit}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-xl hover:shadow-lg hover:shadow-[#1875FF]/25 hover:scale-[1.01] transition-all"
            >
              <Sparkles className="w-4 h-4" />
              Research My Company
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={onSkip}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Skip and fill manually
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
